export interface CalendarEventTarget {
  eventId: string;
  occurrenceStart?: string;
}

export const CALENDAR_EVENT_TARGET_CHANGE = 'calendar:event-target-change';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function normalizeCalendarEventTarget(eventId: unknown, occurrenceStart?: unknown): CalendarEventTarget | null {
  if (typeof eventId !== 'string' || !UUID.test(eventId)) return null;
  if (occurrenceStart === undefined || occurrenceStart === null) return { eventId };
  if (typeof occurrenceStart !== 'string' || !/^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d+)?(?:Z|[+-]\d\d:\d\d)$/.test(occurrenceStart)
    || !Number.isFinite(Date.parse(occurrenceStart))) return null;
  return { eventId, occurrenceStart: new Date(occurrenceStart).toISOString() };
}

export function calendarEventTargetFromUrl(url: string): CalendarEventTarget | null {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.pathname !== '/calendar' && parsed.pathname !== '/calendar/') return null;
    return normalizeCalendarEventTarget(parsed.searchParams.get('eventId'), parsed.searchParams.get('occurrenceStart'));
  } catch { return null; }
}

interface Request {
  target: CalendarEventTarget;
  resolve: () => void;
  reject: (error: Error) => void;
  timer: number;
}
let pending: Request | null = null;
// Capture before the app's route writer replaces query parameters during boot.
let initialTarget = typeof window !== 'undefined' ? calendarEventTargetFromUrl(window.location.href) : null;

export function queueCalendarEventTarget(target: CalendarEventTarget): Promise<void> {
  const normalized = normalizeCalendarEventTarget(target.eventId, target.occurrenceStart);
  if (!normalized) return Promise.reject(new Error('Invalid calendar event destination'));
  if (pending) settleCalendarEventTarget(pending, new Error('Another calendar event was opened'));
  initialTarget = null;
  return new Promise((resolve, reject) => {
    const request: Request = {
      target: normalized, resolve, reject,
      timer: window.setTimeout(() => settleCalendarEventTarget(request, new Error('Could not open this calendar event')), 15000),
    };
    pending = request;
    window.dispatchEvent(new Event(CALENDAR_EVENT_TARGET_CHANGE));
  });
}

export function readCalendarEventTarget(): Request | null {
  if (!pending && initialTarget) {
    const target = initialTarget;
    initialTarget = null;
    pending = { target, resolve: () => {}, reject: () => {}, timer: 0 };
  }
  return pending;
}

export function isCurrentCalendarEventTarget(request: Request): boolean { return pending === request; }

export function settleCalendarEventTarget(request: Request, error?: Error): void {
  if (pending !== request) return;
  window.clearTimeout(request.timer);
  pending = null;
  const url = new URL(window.location.href);
  if (url.searchParams.get('eventId') === request.target.eventId) {
    url.searchParams.delete('eventId');
    url.searchParams.delete('occurrenceStart');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }
  if (error) request.reject(error);
  else request.resolve();
}
